import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../App";
import { api } from "../App";
import { Button } from "../components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";
import LegalModal from "../components/LegalModal";
import LegalAcceptance from "../components/LegalAcceptance";
import CountryCodeSelect from "../components/CountryCodeSelect";
import { normalizeInternationalPhone, parseInternationalPhone } from "../lib/internationalPhone";
import { toast } from "@/lib/toast";
import { MessageCircle, ArrowLeft, Loader2 } from "lucide-react";

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, user } = useAuth();
  
  const [step, setStep] = useState("phone"); // phone | otp
  const [country, setCountry] = useState("CO"); // ISO country, not calling code: multiple countries share +1.
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [legalModal, setLegalModal] = useState(null); // "terms" | "privacy" | null
  const [requiresLegalAcceptance, setRequiresLegalAcceptance] = useState(true);
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);
  
  const [isRecovery, setIsRecovery] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [recoveryCountry, setRecoveryCountry] = useState("CO");
  const [recoveryPhone, setRecoveryPhone] = useState("");
  const [acquisition, setAcquisition] = useState({});

  useEffect(() => {
    const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_content"];
    const incoming = Object.fromEntries(keys.map(key => [key, searchParams.get(key)]).filter(([, value]) => value));
    let merged = incoming;
    try {
      const stored = JSON.parse(window.sessionStorage.getItem("aorra_acquisition") || "{}");
      merged = { ...stored, ...incoming };
      if (Object.keys(incoming).length) {
        window.sessionStorage.setItem("aorra_acquisition", JSON.stringify(merged));
      }
    } catch (_error) {
      // Privacy modes may disable storage. Attribution is optional and must
      // never prevent authentication.
    }
    setAcquisition(merged);
  }, [searchParams]);

  useEffect(() => {
    const fragmentParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const channelToken = searchParams.get("channel_token") || fragmentParams.get("channel_token");
    if (!channelToken || user) return;
    let active = true;
    const exchange = async () => {
      setLoading(true);
      try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const response = await api.post("/auth/channel-login/exchange", {
          token: channelToken,
          timezone,
        });
        if (!active) return;
        login(response.data.access_token, response.data.user, response.data.session);
        toast.success("¡Bienvenido a Aorra!");
        navigate("/dashboard", { replace: true });
      } catch (error) {
        if (active && error.response?.data?.detail?.code === "legal_acceptance_required") {
          setRequiresLegalAcceptance(true);
          setLegalAccepted(false);
          setStep("consent");
        } else if (active) toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || "El enlace expiró o ya fue utilizado");
      } finally {
        if (active) setLoading(false);
      }
    };
    exchange();
    return () => { active = false; };
  }, [searchParams, user, login, navigate]);

  // Redirect if already logged in
  if (user) {
    navigate("/dashboard");
    return null;
  }

  const fullPhone = normalizeInternationalPhone(phone, country);

  const fullRecoveryPhone = normalizeInternationalPhone(recoveryPhone, recoveryCountry);

  const updatePhone = (value, recovering = false) => {
    (recovering ? setRecoveryPhone : setPhone)(value);
    if (/^(\+|00)/.test(value.trim())) {
      const parsed = parseInternationalPhone(value, recovering ? recoveryCountry : country);
      if (parsed?.country) (recovering ? setRecoveryCountry : setCountry)(parsed.country);
    }
  };

  const handleSendRecoveryOTP = async (e) => {
    if (e) e.preventDefault();
    if (!recoveryEmail || !recoveryEmail.includes("@")) {
      toast.error("Ingresa un correo electrónico válido");
      return;
    }
    if (!fullRecoveryPhone) {
      toast.error("Revisa el país y escribe un número de WhatsApp válido");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/recovery/send-otp", { email: recoveryEmail, new_phone: fullRecoveryPhone });
      toast.success("Código enviado a tu correo");
      setStep("otp");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar código");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyRecoveryOTP = async () => {
    if (otp.length !== 6) {
      toast.error("Ingresa el código completo de 6 dígitos");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/recovery/verify-otp", {
        email: recoveryEmail,
        new_phone: fullRecoveryPhone,
        otp: otp
      });
      toast.success("Cuenta recuperada con éxito. Ya puedes iniciar sesión con tu nuevo número.");
      setIsRecovery(false);
      setStep("phone");
      setPhone(recoveryPhone);
      setCountry(recoveryCountry);
      setRecoveryEmail("");
      setRecoveryPhone("");
      setOtp("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Código inválido o expirado");
    } finally {
      setLoading(false);
    }
  };

  const handleSendOTP = async (e) => {
    if (e) e.preventDefault();
    if (!fullPhone) {
      toast.error("Revisa el país y escribe un número de teléfono válido");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post("/auth/send-otp", { phone: fullPhone });
      if (response.data.whatsapp_sent === false && !response.data.local_development) {
        toast.error("No pudimos enviar el código por WhatsApp. Revisa el país y el número e inténtalo de nuevo.");
        return;
      }
      setRequiresLegalAcceptance(response.data.requires_legal_acceptance !== false);
      setLegalAccepted(false);
      setOtp("");
      toast.success("Código OTP enviado a tu WhatsApp");
      setStep("otp");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      toast.error("Ingresa el código completo de 6 dígitos");
      return;
    }
    if (requiresLegalAcceptance && !legalAccepted) {
      toast.error("Acepta los Términos y Condiciones y la Política de Privacidad para ingresar.");
      return;
    }

    setLoading(true);
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await api.post("/auth/verify-otp", {
        phone: fullPhone,
        otp: otp,
        timezone: timezone,
        accept_legal: legalAccepted,
        remember_device: rememberDevice,
        ...acquisition,
      });
      
      login(response.data.access_token, response.data.user, response.data.session);
      toast.success("¡Bienvenido a Aorra!");
      navigate("/dashboard");
    } catch (error) {
      if (error.response?.data?.detail?.code === "legal_acceptance_required") setRequiresLegalAcceptance(true);
      toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || "Código inválido o expirado");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyChannel = async () => {
    if (!legalAccepted || loading) return;
    setLoading(true);
    try {
      const fragmentParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
      const token = searchParams.get("channel_token") || fragmentParams.get("channel_token");
      const response = await api.post("/auth/channel-login/exchange", {
        token, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, accept_legal: true,
        remember_device: rememberDevice,
      });
      login(response.data.access_token, response.data.user, response.data.session);
      toast.success("¡Bienvenido a Aorra!");
      navigate("/dashboard", { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || "El enlace expiró o ya fue utilizado");
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-[#F5F5F5] min-h-screen flex flex-col items-center justify-start sm:justify-center py-8 sm:py-6 px-4 relative font-['Manrope','Inter',sans-serif]">
      
      {/* Background Decoration Elements */}
      <div className="fixed top-[-10%] left-[-5%] w-[40vw] h-[40vw] rounded-full bg-[#396BE9]/5 blur-[120px] -z-10 animate-pulse"></div>
      <div className="fixed bottom-[-10%] right-[-5%] w-[35vw] h-[35vw] rounded-full bg-[#2B50AF]/5 blur-[120px] -z-10 animate-pulse" style={{animationDelay: '1s'}}></div>

      <main className="w-full min-w-0 max-w-[480px] space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700 z-10">
        
        {/* Brand Identity / Logo Section */}
        <div className="flex flex-col items-center gap-4 text-center">
          <img src="/logo-claro.png" alt="Aorra Logo" className="h-8 mb-2 object-contain" />
          <div className="space-y-2">
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#1B2559]">
              Ingresa a tu cuenta 👋
            </h1>
            <p className="text-[#545454]">
              Te enviaremos un código a tu WhatsApp
            </p>
          </div>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-[20px] p-5 sm:p-8 shadow-[0_4px_20px_rgba(27,37,89,0.06)] transition-all duration-300 relative border border-[#E1E9FC]">
          {isRecovery ? (
            step === "phone" ? (
              <form onSubmit={handleSendRecoveryOTP} className="space-y-8">
                <div className="flex justify-between items-center mb-2">
                  <h2 className="text-xl font-bold text-[#2B50AF]">Recuperar Cuenta</h2>
                  <button type="button" aria-label="Volver al inicio de sesión" onClick={() => setIsRecovery(false)} className="text-[#545454] hover:text-[#396BE9] transition-colors">
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="space-y-3">
                  <label htmlFor="recovery-email" className="block text-sm font-semibold text-[#1B2559]">
                    Correo Electrónico Registrado
                  </label>
                  <input 
                    id="recovery-email"
                    className="w-full h-14 px-4 bg-[#F5F5F5] rounded-xl border border-[#E1E9FC] focus:!ring-[#396BE9]/20 focus:!border-[#396BE9] focus:bg-white transition-all text-base font-medium placeholder:text-gray-400 outline-none text-[#1B2559]"
                    placeholder="tu@correo.com" 
                    type="email"
                    value={recoveryEmail}
                    onChange={(e) => setRecoveryEmail(e.target.value)}
                  />
                </div>

                <div className="space-y-3">
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-[#1B2559]">País</p>
                    <CountryCodeSelect value={recoveryCountry} onValueChange={setRecoveryCountry} label="País e indicativo de recuperación" />
                  </div>
                  <label htmlFor="recovery-phone" className="block text-sm font-semibold text-[#1B2559]">
                    Nuevo Número de WhatsApp
                  </label>
                      <input 
                        id="recovery-phone"
                        className="w-full h-14 px-4 bg-[#F5F5F5] rounded-xl border border-[#E1E9FC] focus:!ring-[#396BE9]/20 focus:!border-[#396BE9] focus:bg-white transition-all text-base font-medium placeholder:text-gray-400 outline-none text-[#1B2559]"
                        placeholder="Número de celular"
                        type="tel"
                        autoComplete="tel-national"
                        aria-label="Nuevo número de WhatsApp"
                        aria-describedby="recovery-phone-help"
                        value={recoveryPhone}
                        onChange={(e) => updatePhone(e.target.value, true)}
                      />
                  <p id="recovery-phone-help" className="text-xs leading-relaxed text-[#545454] break-words">{fullRecoveryPhone ? `Número de recuperación: ${fullRecoveryPhone}` : recoveryCountry === "AR" ? "Escribe tu celular con código de área; usaremos el formato de WhatsApp +54 9…" : "También puedes pegar tu número completo con + e indicativo."}</p>
                </div>

                <div className="pt-2">
                  <button 
                    className="w-full h-14 bg-[#396BE9] text-white rounded-full font-bold flex items-center justify-center gap-3 shadow-[0_12px_24px_rgba(57,107,233,0.2)] hover:shadow-[0_16px_32px_rgba(57,107,233,0.3)] hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 disabled:opacity-70 disabled:scale-100" 
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Enviar Código al Correo"}
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-8 animate-in fade-in zoom-in-95 duration-300">
                <button
                  onClick={() => setStep("phone")}
                  className="flex items-center gap-2 text-[#545454] hover:text-[#396BE9] transition-colors mb-2 text-sm font-medium"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Volver
                </button>
                <div className="text-center mb-6">
                  <p className="text-[#545454] text-sm mb-1">Enviamos un código de recuperación a</p>
                  <p className="font-bold text-[#2B50AF] tracking-wide text-lg break-words">{recoveryEmail}</p>
                </div>
                <div className="flex justify-center py-2">
                  <InputOTP maxLength={6} value={otp} onChange={setOtp} containerClassName="w-full min-w-0 justify-center">
                    <InputOTPGroup className="w-full max-w-[348px] gap-1.5 sm:gap-2">
                      {[0, 1, 2, 3, 4, 5].map((index) => (
                        <InputOTPSlot key={index} index={index} className="min-w-0 w-auto flex-1 h-12 sm:h-14 text-lg sm:text-2xl font-bold bg-[#F5F5F5] text-[#2B50AF] border-transparent rounded-xl ring-0 focus:ring-2 focus:ring-[#396BE9]/40 focus:bg-white transition-all" />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <div className="pt-4">
                  <button 
                    onClick={handleVerifyRecoveryOTP}
                    className="w-full h-14 bg-[#396BE9] text-white rounded-full font-bold flex items-center justify-center gap-3 shadow-[0_12px_24px_rgba(57,107,233,0.2)] hover:shadow-[0_16px_32px_rgba(57,107,233,0.3)] hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 disabled:opacity-70 disabled:scale-100" 
                    disabled={loading || otp.length !== 6}
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Verificar Código"}
                  </button>
                </div>
              </div>
            )
          ) : step === "phone" ? (
            <form onSubmit={handleSendOTP} className="space-y-8">
              {/* WhatsApp Input Group */}
              <div className="space-y-3">
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-[#1B2559]">País</p>
                  <CountryCodeSelect value={country} onValueChange={setCountry} />
                </div>
                <label htmlFor="login-phone" className="block text-sm font-semibold text-[#1B2559]">
                  Número de WhatsApp
                </label>
                    <input 
                      id="login-phone"
                      className="w-full h-14 px-4 bg-[#F5F5F5] rounded-xl border border-[#E1E9FC] focus:!ring-[#396BE9]/20 focus:!border-[#396BE9] focus:bg-white transition-all text-base font-medium placeholder:text-gray-400 outline-none text-[#1B2559]"
                      type="tel"
                      autoComplete="tel-national"
                      aria-label="Número de WhatsApp"
                      aria-describedby="login-phone-help"
                      value={phone}
                      onChange={(e) => updatePhone(e.target.value)}
                    />
                <p id="login-phone-help" className="text-xs leading-relaxed text-[#545454] break-words">{fullPhone ? `Enviaremos el código a ${fullPhone}` : country === "AR" ? "Escribe tu celular con código de área; usaremos el formato de WhatsApp +54 9…" : "También puedes pegar tu número completo con + e indicativo."}</p>
              </div>

              {/* Action Button */}
              <div className="pt-2">
                <button 
                  className="w-full h-14 bg-[#396BE9] text-white rounded-full font-bold flex items-center justify-center gap-3 shadow-[0_12px_24px_rgba(57,107,233,0.2)] hover:shadow-[0_16px_32px_rgba(57,107,233,0.3)] hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 disabled:opacity-70 disabled:scale-100" 
                  type="submit"
                  disabled={loading}
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <span className="material-symbols-outlined" style={{fontVariationSettings: "'FILL' 1"}}>chat</span>
                      Enviar Código
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-8 animate-in fade-in zoom-in-95 duration-300">
              <button
                onClick={() => { setStep("phone"); setOtp(""); setLegalAccepted(false); setRememberDevice(false); setRequiresLegalAcceptance(true); }}
                className="flex items-center gap-2 text-[#545454] hover:text-[#396BE9] transition-colors mb-2 text-sm font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                Cambiar número
              </button>

              {step === "consent" ? <p className="text-center text-sm text-[#545454]">Antes de ingresar por primera vez, revisa y acepta los documentos de Aorra.</p> : <>
              <div className="text-center mb-6">
                <p className="text-[#545454] text-sm mb-1">Enviamos un código de 6 dígitos a</p>
                <p className="font-bold text-[#2B50AF] tracking-wide text-lg">{fullPhone}</p>
              </div>

              <div className="flex justify-center py-2">
                <InputOTP 
                  maxLength={6} 
                  value={otp} 
                  onChange={setOtp}
                  data-testid="otp-input"
                  containerClassName="w-full min-w-0 justify-center"
                >
                  <InputOTPGroup className="w-full max-w-[348px] gap-1.5 sm:gap-2">
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <InputOTPSlot 
                        key={index} 
                        index={index} 
                        className="min-w-0 w-auto flex-1 h-12 sm:h-14 text-lg sm:text-2xl font-bold bg-[#F5F5F5] text-[#2B50AF] border-transparent rounded-xl ring-0 focus:ring-2 focus:ring-[#396BE9]/40 focus:bg-white transition-all"
                      />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              </>}

              {step !== "consent" && (
                <label className="flex items-start gap-3 rounded-xl border border-[#E1E9FC] bg-[#F8FAFF] p-4 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberDevice}
                    onChange={(event) => setRememberDevice(event.target.checked)}
                    disabled={loading}
                    className="mt-1 h-4 w-4 rounded border-[#9DB4EE] text-[#396BE9] focus:ring-[#396BE9]"
                    data-testid="remember-device-checkbox"
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-[#1B2559]">Mantener mi sesión en este dispositivo durante 7 días</span>
                    <span className="mt-1 block text-xs leading-relaxed text-[#545454]">Actívalo solo en un celular o computador personal. No guardaremos tu código ni una contraseña.</span>
                  </span>
                </label>
              )}

              {requiresLegalAcceptance && <LegalAcceptance checked={legalAccepted} onChange={setLegalAccepted} onOpen={setLegalModal} disabled={loading} />}

              <div className="pt-4">
                <button 
                  onClick={step === "consent" ? handleVerifyChannel : handleVerifyOTP}
                  className="w-full h-14 bg-[#396BE9] text-white rounded-full font-bold flex items-center justify-center gap-3 shadow-[0_12px_24px_rgba(57,107,233,0.2)] hover:shadow-[0_16px_32px_rgba(57,107,233,0.3)] hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 disabled:opacity-70 disabled:scale-100" 
                  disabled={loading || (step !== "consent" && otp.length !== 6) || (requiresLegalAcceptance && !legalAccepted)}
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    step === "consent" ? "Aceptar e ingresar" : "Verificar Código"
                  )}
                </button>
              </div>

              {step !== "consent" && <p className="text-center text-sm text-[#545454]">
                ¿No recibiste el código?{" "}
                <button 
                  onClick={handleSendOTP}
                  className="text-[#396BE9] font-semibold hover:underline"
                  disabled={loading}
                >
                  Reenviar
                </button>
              </p>}
            </div>
          )}

          {/* Social/Secondary Intent */}
          {step === "phone" && (
            <div className="mt-8 pt-6 border-t border-[#E1E9FC] text-center">
              <div className="inline-flex flex-col items-center gap-1 group">
                <span className="text-[#545454] text-sm">¿No tienes cuenta?</span>
                <span className="text-[#396BE9] font-semibold text-sm group-hover:underline transition-all">
                  Se creará automáticamente
                </span>
                <button 
                  type="button"
                  onClick={() => setIsRecovery(true)}
                  className="text-red-500 font-semibold text-xs mt-2 hover:underline transition-all"
                >
                  Ya no tengo acceso a mi WhatsApp
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Legal Links Footer */}
      <footer className="w-full flex flex-col items-center gap-2 mt-8 pb-6 z-10">
        <div className="flex gap-5 flex-wrap justify-center">
          <button
            type="button"
            onClick={() => setLegalModal("terms")}
            className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest hover:text-[#396BE9] transition-colors"
          >
            Términos y condiciones
          </button>
          <span className="text-gray-300 text-xs">·</span>
          <button
            type="button"
            onClick={() => setLegalModal("privacy")}
            className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest hover:text-[#396BE9] transition-colors"
          >
            Política de privacidad
          </button>
        </div>
      </footer>

      {/* Legal Modal */}
      <LegalModal
        type={legalModal}
        onClose={() => setLegalModal(null)}
      />

    </div>
  );
};

export default LoginPage;
